import { isLocalDevelopment } from "../utils/environment";

export const DEFAULT_SCENE7_EXTENSION = `.scene7Src.dir.json/srcSet/false/width//height//aspectRatio//focalPoint//optimized/true/.cache`;

export const getScene7Extension = (scene7ConfigExt?: string): string => {
  return scene7ConfigExt || DEFAULT_SCENE7_EXTENSION;
};

/**
 * Fetch image source from Scene7 URL
 * @param url - The Scene7 URL to fetch
 * @returns Promise resolving to the processed image source or null if failed
 */
async function fetchImageSrc(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, {});
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    
    if (response.status >= 500) {
      return null;
    }
    
    return data?.src || null;
  } catch (error) {
    return null;
  }
}

/**
 * Process image for display based on environment
 * @param imageSrc - The CMS image path or direct Scene7 URL
 * @param scene7extension - Optional custom Scene7 extension
 * @returns Promise resolving to the appropriate image URL
 */
export async function fetchScene7Image(
  imageSrc: string,
  scene7extension?: string,
): Promise<string> {
  
  // If it's already a processed Scene7 URL (contains assets.volkswagen.com), return as-is
  if (imageSrc.includes('assets.volkswagen.com')) {
    return imageSrc;
  }
  
  // In local development, try to fetch from QA Scene7 first
  if (isLocalDevelopment()) {
    const extension = getScene7Extension(scene7extension);
    const baseUrl = 'https://cs-int-vw-us.lighthouselabs.eu';
    const scene7Url = `${baseUrl}${imageSrc}${extension}`;
    
    try {
      const processedImageSrc = await fetchImageSrc(scene7Url);
      
      if (processedImageSrc) {
        return processedImageSrc;
      }
    } catch (error) {
      // Silent fallback
    }
    
    // Fallback to QA direct URL if Scene7 fails
    return `${baseUrl}${imageSrc}`;
  }

  // For production, try to fetch from Scene7
  const extension = getScene7Extension(scene7extension);
  const scene7Url = `${imageSrc}${extension}`;
  
  try {
    const processedImageSrc = await fetchImageSrc(scene7Url);
    
    if (processedImageSrc) {
      return processedImageSrc;
    }
  } catch (error) {
    // Silent fallback
  }
  
  // In production, return the original path (CMS will handle it)
  return imageSrc;
}